import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { PhysicsAggregate } from '@babylonjs/core/Physics/v2/physicsAggregate';
import { PhysicsShapeType } from '@babylonjs/core/Physics/v2/IPhysicsEnginePlugin';
import { ActionManager } from '@babylonjs/core/Actions/actionManager';
import { ExecuteCodeAction } from '@babylonjs/core/Actions/directActions';

function moveTowards(current: number, target: number, rate: number): number {
  if (target > current) {
    return Math.min(target, current + rate);
  } else {
    return Math.max(target, current - rate);
  }
}

class PlayerMovementController {
  game: any;
  impostorMesh: AbstractMesh | null;
  physicsAggregate: PhysicsAggregate | null;
  inputMap: Map<string, boolean>;
  moveSpeed: number;
  sprintMultiplier: number;
  rotationSpeed: number;
  jumpForce: number;
  _isGrounded: boolean;
  _groundCheckDistance: number;
  _currentSpeed: number;
  _isSprinting: boolean;

  keyForward: string;
  keyBackward: string;
  keyLeft: string;
  keyRight: string;
  keySprint: string;
  keyJump: string;

  constructor(game: any) {
    this.game = game;
    this.impostorMesh = null;
    this.physicsAggregate = null;
    this.inputMap = new Map();
    this.moveSpeed = 1.8;
    this.sprintMultiplier = 1.6;
    this.rotationSpeed = 6.0;
    this.jumpForce = 6.0;
    this._isGrounded = false;
    this._groundCheckDistance = 0.15;
    this._currentSpeed = this.moveSpeed;
    this._isSprinting = false;

    this.keyForward = 'w';
    this.keyBackward = 's';
    this.keyLeft = 'a';
    this.keyRight = 'd';
    this.keySprint = 'shift';
    this.keyJump = ' ';
  }

  init(scene: any, modelMesh: AbstractMesh) {
    const impostorMesh = MeshBuilder.CreateCapsule(
      'CharacterImpostor',
      { height: 2, radius: 0.5 },
      scene
    );
    impostorMesh.visibility = 0.1;
    impostorMesh.rotationQuaternion = Quaternion.Identity();
    impostorMesh.checkCollisions = true;
    this.impostorMesh = impostorMesh;

    modelMesh.parent = impostorMesh;
    modelMesh.position.y = -1;
    modelMesh.rotationQuaternion = Quaternion.RotationAxis(Vector3.Up(), Math.PI);

    this.physicsAggregate = new PhysicsAggregate(
      impostorMesh,
      PhysicsShapeType.CAPSULE,
      { mass: 1, friction: 0.5 },
      scene
    );
    this.physicsAggregate.body.setMassProperties({ inertia: Vector3.ZeroReadOnly });
    this.physicsAggregate.body.setAngularDamping(100);
    this.physicsAggregate.body.setLinearDamping(10);

    this._bindInput(scene);
  }

  _bindInput(scene: any) {
    if (!scene.actionManager) {
      scene.actionManager = new ActionManager(scene);
    }

    scene.actionManager.registerAction(
      new ExecuteCodeAction(ActionManager.OnKeyDownTrigger, (e: any) => {
        this.inputMap.set(e.sourceEvent.key.toLowerCase(), true);
      })
    );
    scene.actionManager.registerAction(
      new ExecuteCodeAction(ActionManager.OnKeyUpTrigger, (e: any) => {
        this.inputMap.set(e.sourceEvent.key.toLowerCase(), false);
      })
    );

    document.addEventListener('keydown', (e) => {
      this.inputMap.set(e.key.toLowerCase(), true);
      if (e.key === 'Shift') this.inputMap.set('shift', true);
    });
    document.addEventListener('keyup', (e) => {
      this.inputMap.set(e.key.toLowerCase(), false);
      if (e.key === 'Shift') this.inputMap.set('shift', false);
    });
  }

  getTransform(): AbstractMesh | null {
    return this.impostorMesh;
  }

  isMoving(): boolean {
    return (
      this.inputMap.get(this.keyForward) ||
      this.inputMap.get(this.keyBackward) ||
      this.inputMap.get(this.keyLeft) ||
      this.inputMap.get(this.keyRight)
    ) as boolean;
  }

  isSprinting(): boolean {
    return this._isSprinting;
  }

  isGrounded(): boolean {
    return this._isGrounded;
  }

  update(delta: number) {
    if (!this.impostorMesh || !this.physicsAggregate || !this.game.thirdPersonCameraController) return;

    const cameraController = this.game.thirdPersonCameraController;
    if (!cameraController.camera) return;

    this._checkGrounded();

    this._isSprinting = !!(this.inputMap.get(this.keySprint) && this.isMoving());
    this._currentSpeed = this._isSprinting
      ? this.moveSpeed * this.sprintMultiplier
      : this.moveSpeed;

    const angle180 = Math.PI;
    const angle45 = angle180 / 4;
    const angle90 = angle180 / 2;
    const angle135 = angle45 + angle90;

    const forward = cameraController.getForwardDirection();
    const rot = Quaternion.FromLookDirectionLH(forward, Vector3.Up());

    let rotation = 0;
    const fwd = this.inputMap.get(this.keyForward);
    const bwd = this.inputMap.get(this.keyBackward);
    const lft = this.inputMap.get(this.keyLeft);
    const rgt = this.inputMap.get(this.keyRight);

    if (bwd && !rgt && !lft) rotation = angle180;
    if (lft && !fwd && !bwd) rotation = -angle90;
    if (rgt && !fwd && !bwd) rotation = angle90;
    if (fwd && rgt) rotation = angle45;
    if (fwd && lft) rotation = -angle45;
    if (bwd && rgt) rotation = angle135;
    if (bwd && lft) rotation = -angle135;

    rot.multiplyInPlace(Quaternion.RotationAxis(Vector3.Up(), rotation));

    if (fwd || bwd || lft || rgt) {
      const impostorQuaternion = this.impostorMesh.rotationQuaternion;
      if (impostorQuaternion) {
        Quaternion.SlerpToRef(
          impostorQuaternion,
          rot,
          this.rotationSpeed * delta,
          impostorQuaternion
        );
      }

      this.impostorMesh.translate(
        new Vector3(0, 0, -1),
        this._currentSpeed * delta
      );
      this.physicsAggregate.body.setTargetTransform(
        this.impostorMesh.absolutePosition,
        impostorQuaternion!
      );
    }

    if (this.inputMap.get(this.keyJump) && this._isGrounded) {
      this.physicsAggregate.body.applyImpulse(
        new Vector3(0, this.jumpForce, 0),
        this.impostorMesh.getAbsolutePosition()
      );
    }
  }

  _checkGrounded() {
    if (!this.impostorMesh || !this.physicsAggregate) return;
    const scene = this.impostorMesh.getScene();
    if (!scene) return;

    const pos = this.impostorMesh.getAbsolutePosition();
    const rayOrigin = new Vector3(pos.x, pos.y - 0.9, pos.z);
    const rayDir = Vector3.Down();
    const rayLength = this._groundCheckDistance + 0.1;

    const pickResult = scene.pickWithRay(
      { origin: rayOrigin, direction: rayDir, length: rayLength } as any,
      (mesh: any) => mesh.isPickable && mesh.checkCollisions
    );

    this._isGrounded = !!(pickResult && pickResult.hit);
  }

  teleport(position: Vector3) {
    if (!this.impostorMesh || !this.physicsAggregate) return;
    this.impostorMesh.position.copyFrom(position);
    this.physicsAggregate.body.setTargetTransform(position, this.impostorMesh.rotationQuaternion || Quaternion.Identity());
  }

  dispose() {
    if (this.physicsAggregate) {
      this.physicsAggregate.dispose();
    }
    if (this.impostorMesh) {
      this.impostorMesh.dispose();
    }
    this.impostorMesh = null;
    this.physicsAggregate = null;
  }
}

export { PlayerMovementController };
